import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { organisationLabel } from 'src/app/infrastructure/functions/user-name.function';
import { AutocompleteControlComponent } from 'src/app/features/shared/controls/container/autocomplete-control/autocomplete-control.component';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { ButtonsRowComponent } from 'src/app/features/shared/controls/container/buttons-row/buttons-row.component';
import { ChipsControlComponent, TChipItem } from 'src/app/features/shared/controls/container/chips-control/chips-control.component';
import { IconComponent } from 'src/app/features/shared/icons/container/icon.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { RichTextControlComponent } from 'src/app/features/shared/controls/container/rich-text-control/rich-text-control.component';
import { SlideToggleControlComponent } from 'src/app/features/shared/controls/container/slide-toggle-control/slide-toggle-control.component';
import { buttonsRowAlign } from 'src/app/features/shared/controls/models/buttons-row-options';
import { IMessageAudienceDto, messageAudienceDto } from 'src/assets/common/models/message-audience-dto.g';
import { IMessageDto } from 'src/assets/common/models/message-dto.g';
import { TMessageAudienceKind, messageAudienceKind } from 'src/assets/common/enums/message-audience-kind.g';

// Projectbericht (send-from-project), taakbericht (send-from-task: projectAMemberGuid + taskGuid) of domainbericht
// (send-from-domain).
export interface IMessageSendDialogData {
    projectAMemberGuid: string | undefined;
    domainAUserGuid: string | undefined;
    taskGuid?: string | undefined;
}

// Doelgroep in de autocomplete en als chip. key = soort + guid (projectleden hebben geen guid).
type TAudienceOption = { key: string; kind: TMessageAudienceKind; guid: string | null; label: string; detail: string | null };

// Nieuw bericht: doelgroep (chips; kandidaten = projectleden, functies met actieve leden en losse leden, via
// GET /api/Message/candidates-from-*), onderwerp, tekst (richtext) en of bevestiging gevraagd wordt. ✓ verzendt
// (POST /api/Message/send-from-*) en sluit met het bericht; daarna is het niet meer te wijzigen. Bij een fout blijft
// de dialoog open (de melding komt van de centrale foutafhandeling). undefined = annuleren.
@Component({
    selector: 'app-message-send-dialog',
    templateUrl: './message-send-dialog.component.html',
    styleUrls: ['./message-send-dialog.component.scss'],
    standalone: true,
    imports: [
        AutocompleteControlComponent,
        ButtonActionComponent,
        ButtonsRowComponent,
        ChipsControlComponent,
        IconComponent,
        InputControlComponent,
        RichTextControlComponent,
        SlideToggleControlComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageSendDialogComponent
    implements OnInit
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __destroyRef = inject(DestroyRef);
    private readonly __dialogRef = inject<MatDialogRef<MessageSendDialogComponent, IMessageDto>>(MatDialogRef);
    private readonly __data = inject<IMessageSendDialogData>(MAT_DIALOG_DATA);
    private readonly __search$ = new Subject<void>();

    readonly buttonsRowAlign = buttonsRowAlign;
    readonly optionField = { display: 'label', value: 'key' };

    readonly options$$ = signal<TAudienceOption[]>([]);
    readonly audiences$$ = signal<TAudienceOption[]>([]);
    readonly chips$$ = computed<TChipItem[]>(() => this.audiences$$().map(a => ({
        key: a.key,
        label: a.detail ? `${a.label} · ${a.detail}` : a.label,
        icon: a.kind === messageAudienceKind.domainUser ? 'user' : 'users',
    })));
    readonly saving$$ = signal<boolean>(false);
    private readonly __valid$$ = signal<boolean>(false);
    readonly canConfirm$$ = computed(() => this.__valid$$() && this.audiences$$().length > 0 && !this.saving$$());

    control_subject = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(200)]);
    control_body = new FormControl<string | null>(null, [Validators.required]);
    control_acknowledge = new FormControl<boolean>(false, { nonNullable: true });
    control_search = new FormControl<string | null>(null);

    protected readonly _text = {
        title: 'Nieuw bericht',
        audience_label: 'to',
        audience_empty: 'kies minstens één doelgroep',
        search_label: 'add recipients',
        search_placeholder: 'projectleden, functie of persoon',
        projectMembers: 'alle projectleden',
        taskMembers: 'alle taakleden',
        members_suffix: 'leden',
        subject_label: 'subject',
        body_label: 'message',
        acknowledge_label: 'bevestiging van ontvangst vragen',
        remove: 'verwijderen',
        cancel: 'Annuleren',
        confirm: 'Verzenden (daarna niet meer te wijzigen)',
    };

    private get __isProject(): boolean {
        return !!this.__data.projectAMemberGuid;
    }

    ngOnInit(): void {
        this.control_subject.valueChanges.pipe(takeUntilDestroyed(this.__destroyRef)).subscribe(() => this.__valid_sync());
        this.control_body.valueChanges.pipe(takeUntilDestroyed(this.__destroyRef)).subscribe(() => this.__valid_sync());
        this.control_search.valueChanges.pipe(takeUntilDestroyed(this.__destroyRef)).subscribe(() => this.__search$.next());

        this.__search$.pipe(
            debounceTime(250),
            switchMap(() => this.__options_get$()),
            takeUntilDestroyed(this.__destroyRef),
        ).subscribe(list => this.options$$.set(list));

        this.__search$.next();
    }

    emit_optionSelected(option: TAudienceOption): void {
        // Het zoekveld maakt de control zelf leeg (clearOnSelect); dat start ook een nieuwe zoekvraag zonder deze keuze.
        if (!this.audiences$$().some(a => a.key === option.key)) this.audiences$$.update(list => [...list, option]);
    }

    emit_chipRemove(chip: TChipItem): void {
        this.audiences$$.update(list => list.filter(a => a.key !== chip.key));
        this.__search$.next();
    }

    emit_cancel(): void {
        this.__dialogRef.close(undefined);
    }

    emit_confirm(): void {
        if (!this.canConfirm$$()) return;
        this.saving$$.set(true);

        const __body = {
            subject: this.control_subject.value?.trim() ?? '',
            body: this.control_body.value?.trim() ?? '',
            acknowledgeRequired: this.control_acknowledge.value,
            audienceDtos: this.audiences$$().map(a => ({ kind: a.kind, guid: a.guid })),
        };
        const __request = this.__data.taskGuid
            ? this.__apiService.POST('/api/Message/send-from-task', {
                params: { query: { key: this.__data.taskGuid, projectAMemberGuid: this.__data.projectAMemberGuid } },
                body: __body as any,
            })
            : this.__isProject
            ? this.__apiService.POST('/api/Message/send-from-project', {
                params: { query: { projectAMemberGuid: this.__data.projectAMemberGuid } },
                body: __body as any,
            })
            : this.__apiService.POST('/api/Message/send-from-domain', {
                params: { query: { domainAUserGuid: this.__data.domainAUserGuid } },
                body: __body as any,
            });

        from(__request).subscribe({
            next: r => {
                this.saving$$.set(false);
                const __dto = (r.data as any)?.modelDto as IMessageDto | undefined;
                if (r.error || !__dto) return;
                this.__dialogRef.close(__dto);
            },
            error: () => this.saving$$.set(false),
        });
    }

    private __valid_sync(): void {
        this.__valid$$.set(!!this.control_subject.value?.trim() && !!this.control_body.value?.trim()
            && this.control_subject.valid && this.control_body.valid);
    }

    private __options_get$() {
        const __filter = this.control_search.value?.trim() || undefined;
        const __request = this.__data.taskGuid
            ? this.__apiService.GET('/api/Message/candidates-from-task', {
                params: { query: { key: this.__data.taskGuid, filter: __filter, projectAMemberGuid: this.__data.projectAMemberGuid } },
            })
            : this.__isProject
            ? this.__apiService.GET('/api/Message/candidates-from-project', {
                params: { query: { filter: __filter, projectAMemberGuid: this.__data.projectAMemberGuid } },
            })
            : this.__apiService.GET('/api/Message/candidates-from-domain', {
                params: { query: { filter: __filter, domainAUserGuid: this.__data.domainAUserGuid } },
            });

        return from(__request).pipe(
            map(r => ((r.data as any)?.list ?? []) as IMessageAudienceDto[]),
            map(list => list
                .map(c => this.__option_get(c))
                // Al gekozen doelgroepen niet nog eens aanbieden.
                .filter(o => !this.audiences$$().some(a => a.key === o.key))),
            catchError(() => of([] as TAudienceOption[])),
        );
    }

    private __option_get(c: IMessageAudienceDto): TAudienceOption {
        const __kind = (c[messageAudienceDto.kind] ?? messageAudienceKind.domainUser) as TMessageAudienceKind;
        const __guid = c[messageAudienceDto.guid] ?? null;
        const __count = c[messageAudienceDto.memberCount];
        const __detail = __kind === messageAudienceKind.domainUser
            ? organisationLabel(c[messageAudienceDto.affiliation], c[messageAudienceDto.organisation_Name])
            : __count != null ? `${__count} ${this._text.members_suffix}` : null;
        return {
            key: `${__kind}:${__guid ?? ''}`,
            kind: __kind,
            guid: __guid,
            label: __kind === messageAudienceKind.projectMembers ? this._text.projectMembers
                : __kind === messageAudienceKind.taskMembers ? this._text.taskMembers
                : c[messageAudienceDto.name] ?? '',
            detail: __detail,
        };
    }
}
